import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Eye, Users, Coins, Compass, MessageCircle, Phone, Star, CheckCircle, Shield, Clock, Wallet, HelpCircle, Calendar, Sparkles, Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';
import heroImage from '../assets/media/Hero-img.jpeg';
import divineVideo from '../assets/media/Vid-1.mp4';

// Divine Darshan Video Section
const DivineDarshanSection = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const videoRef = useRef(null);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const onTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setProgress((current / total) * 100);
      setCurrentTime(formatTime(current));
    }
  };

  const onLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(formatTime(videoRef.current.duration));
    }
  };

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * videoRef.current.duration;
    videoRef.current.currentTime = seekTime;
    setProgress(e.target.value);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-[#2A0505] to-[#3D0A0A] relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12">
          
          <div className="inline-block px-4 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-sm font-bold mb-4">
            திவ்ய தரிசனம்
          </div>
          <h2 className="font-tamil-heading text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-white to-amber-500 mb-4 text-shadow-violet">
            அம்மனின் திவ்ய தரிசனம்
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto rounded-full" />
        </motion.div>

        {/* Video Container - Compact sized as requested */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative max-w-3xl mx-auto group">
          
          {/* Decorative Frame */}
          <div className="absolute -inset-4 bg-gradient-to-r from-amber-600/20 via-yellow-500/10 to-amber-600/20 rounded-[2rem] blur-xl opacity-50" />
          
          <div className="relative rounded-2xl overflow-hidden border-4 border-amber-500/30 shadow-2xl bg-black aspect-video group/player">
            <video
              ref={videoRef}
              src={divineVideo}
              className="w-full h-full object-cover"
              autoPlay
              muted={isMuted}
              loop
              playsInline
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={onLoadedMetadata}
            />

            {/* Custom Overlay Controls */}
            <div className="absolute inset-0 bg-black/0 group-hover/player:bg-black/40 transition-all duration-300">
              
              {/* Play/Pause Center Button (only visible on hover and not seeking) */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={togglePlay}
                  className="w-16 h-16 bg-amber-500/80 hover:bg-amber-500 text-[#2A0505] rounded-full flex items-center justify-center backdrop-blur-sm shadow-xl transition-all opacity-0 group-hover/player:opacity-100 pointer-events-auto transform translate-y-4 group-hover/player:translate-y-0"
                >
                  {isPlaying ? <Pause className="w-8 h-8 fill-current" /> : <Play className="w-8 h-8 fill-current ml-1" />}
                </motion.button>
              </div>

              {/* Bottom Control Bar */}
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover/player:translate-y-0 transition-transform duration-300 bg-gradient-to-t from-black/80 to-transparent">
                
                {/* Timeline Bar (Seek Bar) */}
                <div className="relative mb-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={handleSeek}
                    className="w-full h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-amber-500 hover:h-1.5 transition-all"
                  />
                  <div 
                    className="absolute top-0 left-0 h-1 bg-amber-500 rounded-lg pointer-events-none" 
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <button onClick={togglePlay} className="text-white hover:text-amber-400 transition-colors">
                      {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
                    </button>

                    {/* Volume Controls */}
                    <div className="flex items-center gap-2 group/volume relative">
                      <button onClick={toggleMute} className="text-white hover:text-amber-400 transition-colors">
                        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={volume}
                        onChange={handleVolumeChange}
                        className="w-0 group-hover/volume:w-20 overflow-hidden h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-amber-500 transition-all duration-300"
                      />
                    </div>

                    <div className="text-white text-xs font-mono ml-2">
                      {currentTime} / {duration}
                    </div>
                  </div>

                  <button
                    onClick={toggleFullscreen}
                    className="p-1.5 bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
                  >
                    <Maximize className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Golden Corner Accents */}
          <div className="absolute -top-2 -left-2 text-3xl text-amber-500/40">🔱</div>
          <div className="absolute -top-2 -right-2 text-3xl text-amber-500/40">🔱</div>
          <div className="absolute -bottom-2 -left-2 text-3xl text-amber-500/40">🔱</div>
          <div className="absolute -bottom-2 -right-2 text-3xl text-amber-500/40">🔱</div>
        </motion.div>
      </div>
    </section>
  );
};

const WHATSAPP_NUMBER = "916381326346";
const PHONE_NUMBER = "+91 6381326346";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=வணக்கம்! அம்மனின் அருள்வாக்கு பெற விரும்புகிறேன்.`;

// Official WhatsApp Logo Component
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className} 
    fill="currentColor" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.438 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const pulseGlow = {
  animate: {
    boxShadow: [
    "0 0 20px rgba(251, 191, 36, 0.3)",
    "0 0 40px rgba(251, 191, 36, 0.5)",
    "0 0 20px rgba(251, 191, 36, 0.3)"],

    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const floatAnimation = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Decorative Diya Component
const Diya = ({ className, delay = 0 }) =>
<motion.div
  className={`absolute ${className}`}
  initial={{ opacity: 0, scale: 0 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ delay, duration: 0.5 }}>

    <motion.div
    animate={{
      filter: ["brightness(1)", "brightness(1.3)", "brightness(1)"],
      scale: [1, 1.05, 1]
    }}
    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
    className="text-2xl">

      🪔
    </motion.div>
  </motion.div>;


// Floating Sparkle Component
const FloatingSparkle = ({ className, delay = 0, color = "text-amber-400" }) =>
<motion.div
  className={`absolute ${className}`}
  initial={{ opacity: 0 }}
  animate={{
    opacity: [0, 1, 0],
    y: [0, -20, -40],
    scale: [0.5, 1, 0.5]
  }}
  transition={{
    duration: 3,
    repeat: Infinity,
    delay,
    ease: "easeOut"
  }}>

    <Sparkles className={`w-4 h-4 ${color}`} />
  </motion.div>;


// Header Navigation
const HeaderNav = () =>
<header
  data-testid="header-nav"
  className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-b from-[#7f1d1d]/95 to-transparent backdrop-blur-sm">

    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
      <motion.div
      className="flex items-center gap-2"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}>

        <span className="text-xl">🔱</span>
        <div className="flex flex-col">
          <span className="font-tamil-heading text-transparent bg-clip-text bg-gradient-to-r from-violet-200 via-white to-fuchsia-200 text-base sm:text-lg font-bold text-shadow-violet">மேல்மலையனுர் அங்காளம்மன் அருள்வாக்கு நிலையம்</span>
          <span className="font-tamil-body text-fuchsia-100 text-[10px] sm:text-xs text-shadow-violet">உங்கள் கவலைகளுக்கு அம்மனின் வாக்கு மூலம் நேரடித் தீர்வு</span>
        </div>
      </motion.div>
      <motion.a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-whatsapp-btn"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white text-sm px-4 py-2 rounded-full transition-all shadow-lg shadow-green-500/30">

        <WhatsAppIcon className="w-4 h-4" />
        <span className="hidden sm:inline">WhatsApp</span>
        <span className="text-xs font-medium text-white">{PHONE_NUMBER}</span>
      </motion.a>
    </div>
  </header>;


// Hero Section
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex flex-col justify-center items-center text-center overflow-hidden pt-16">

      {/* Animated Background Gradient */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-[#7f1d1d] via-[#991b1b] to-[#1e3a8a]"
        animate={{
          background: [
            "linear-gradient(135deg, #7f1d1d 0%, #b91c1c 50%, #1e3a8a 100%)",
            "linear-gradient(135deg, #b91c1c 0%, #7f1d1d 50%, #172554 100%)",
            "linear-gradient(135deg, #7f1d1d 0%, #b91c1c 50%, #1e3a8a 100%)"]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }} />

      {/* Background Image - Full Section */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{
            backgroundImage: `url(${heroImage})`
          }} />

        {/* Divine glow and vignette overlay - Deepened for readability */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-[#7f1d1d]/80"
          animate={{
            opacity: [0.8, 0.9, 0.8],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />

        {/* Focus on image - subtle radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(0,0,0,0.4)_100%)]" />
      </div>

      {/* Floating Diyas */}
      <Diya className="top-20 left-4 md:left-10" delay={0.5} />
      <Diya className="top-32 left-8 md:left-20" delay={0.8} />
      <Diya className="bottom-32 left-6 md:left-16" delay={1.1} />
      
      {/* Floating Sparkles */}
      <FloatingSparkle className="top-40 left-20" delay={0} color="text-amber-400" />
      <FloatingSparkle className="top-60 left-10" delay={1} color="text-emerald-400" />
      <FloatingSparkle className="bottom-40 left-24" delay={2} color="text-purple-400" />
      
      {/* Content */}
      <div className="relative z-10 px-6 py-12 max-w-4xl mx-auto md:mr-auto md:ml-12 lg:ml-24">
        {/* Om Symbol */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5, rotate: -180 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, type: "spring" }}
          className="mb-4">

          <motion.span
            className="text-4xl md:text-5xl text-amber-500 drop-shadow-lg"
            animate={{
              textShadow: [
                "0 0 10px rgba(251, 191, 36, 0.5)",
                "0 0 30px rgba(251, 191, 36, 0.8)",
                "0 0 10px rgba(251, 191, 36, 0.5)"]
            }}
            transition={{ duration: 2, repeat: Infinity }}>
            ॐ
          </motion.span>
        </motion.div>

        {/* Logo/Name */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-6 text-left">

          <motion.p
            className="font-tamil-body text-orange-400 text-sm mb-1 flex items-center gap-2"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}>
            <span>🔥</span> ஓம் சக்தி பரா சக்தி <span>🔥</span>
          </motion.p>
          <h1 className="font-tamil-heading text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-fuchsia-400 to-indigo-500 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-2 drop-shadow-2xl scale-110 origin-left text-shadow-violet">
            வாழ்க்கையில்<br />
            அடுத்து என்ன?
          </h1>
          <p className="text-fuchsia-100 text-lg font-bold text-shadow-violet">தெரியாம பயப்படுறீங்களா?</p>
        </motion.div>

        {/* CTA Button */}
        <motion.a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-hero-btn"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.6 }}
      whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(37, 211, 102, 0.5)" }}
      whileTap={{ scale: 0.95 }}
      className="inline-flex items-center gap-3 bg-[#25D366] text-white font-tamil-body font-semibold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 rounded-full shadow-lg shadow-green-500/40 transition-all duration-300">

        <motion.span
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}>

          <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        </motion.span>
        <span>வாட்ஸ்அப்பில் உடனே பேச</span>
      </motion.a>

        {/* Sub text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 font-tamil-body text-white text-base sm:text-lg max-w-md text-left text-shadow-violet leading-relaxed font-semibold">
          பயப்படாதீர்கள்! உங்கள் கஷ்டங்களுக்கான தீர்வையும், 
          இனி வரும் காலத்திற்கான வழிகாட்டலையும் பெறுங்கள்.
        </motion.p>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-6 flex flex-wrap gap-4 text-amber-300/70 text-xs sm:text-sm">

          <motion.span
            className="flex items-center gap-2 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20"
            whileHover={{ scale: 1.05, borderColor: "rgba(251, 191, 36, 0.5)" }}>
            <Shield className="w-4 h-4" /> 100% ரகசியம்
          </motion.span>
          <motion.span
            className="flex items-center gap-2 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20"
            whileHover={{ scale: 1.05, borderColor: "rgba(251, 191, 36, 0.5)" }}>
            <Clock className="w-4 h-4" /> 24 மணி நேரத்தில் பதில்
          </motion.span>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2">

        <div className="w-6 h-10 rounded-full border-2 border-amber-400/50 flex justify-center pt-2">
          <motion.div
            className="w-1.5 h-3 bg-amber-400 rounded-full"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }} />
        </div>
      </motion.div>
    </section>
  );
};


// Services Section
const ServicesSection = () => {
  const services = [
  {
    icon: Users,
    title: 'குடும்பப் பிரச்சனை',
    color: 'from-orange-500 to-red-600',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600'
  },
  {
    icon: Eye,
    title: 'கண் திருஷ்டி',
    color: 'from-blue-500 to-indigo-600',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },
  {
    icon: Wallet,
    title: 'தொழில் நஷ்டம்',
    color: 'from-emerald-500 to-teal-700',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-700'
  },
  {
    icon: HelpCircle,
    title: 'எதிர்காலக் கணிப்பு',
    color: 'from-purple-500 to-magenta-600',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600'
  }];


  return (
    <section
      data-testid="services-section"
      className="relative py-16 px-4 overflow-hidden">

      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, #fef3c7 0%, #fde68a 50%, #fef3c7 100%)"
        }} />

      
      {/* Decorative pattern overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b45309' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />

      {/* Temple pillar decorations */}
      <div className="absolute left-0 top-0 bottom-0 w-8 md:w-16 bg-gradient-to-r from-[#d4a574] to-transparent opacity-50" />
      <div className="absolute right-0 top-0 bottom-0 w-8 md:w-16 bg-gradient-to-l from-[#d4a574] to-transparent opacity-50" />

      {/* Floating Diyas */}
      <Diya className="top-8 right-20" delay={0.2} />
      <Diya className="bottom-8 left-20" delay={0.5} />

      <div className="relative z-10 max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10">

          <motion.span
            className="text-3xl mb-2 block"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}>

            🙏
          </motion.span>
          <h2 className="font-tamil-heading text-transparent bg-clip-text bg-gradient-to-r from-red-800 via-red-700 to-red-800 text-xl sm:text-2xl font-bold">
            நாங்கள் அளிக்கும் தீர்வுகள்:
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4">

          {services.map((service, index) =>
          <motion.div
            key={index}
            variants={fadeInUp}
            whileHover={{
              y: -8,
              scale: 1.03,
              boxShadow: "0 20px 40px rgba(180, 83, 9, 0.2)"
            }}
            data-testid={`service-card-${index}`}
            className="bg-white/95 backdrop-blur-sm border-2 border-orange-400 p-5 rounded-2xl flex flex-col items-center text-center gap-3 transition-all duration-300 shadow-xl box-glow-gold">

              <motion.div
              className={`w-14 h-14 rounded-full ${service.iconBg} flex items-center justify-center shadow-inner`}
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5 }}>

                <service.icon className={`w-7 h-7 ${service.iconColor}`} />
              </motion.div>
              <h3 className="font-tamil-heading text-red-900 text-sm font-bold leading-tight">
                {service.title}
              </h3>
              <div className={`w-12 h-1 rounded-full bg-gradient-to-r ${service.color}`} />
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
  {
    step: '1',
    title: 'மெசேஜ் செய்யவும்',
    desc: 'வாட்ஸ்அப் மூலம் உங்கள் பிரச்சனையை கூறவும்',
    icon: MessageCircle,
    bgColor: 'from-[#25D366] to-[#128C7E]',
    shadowColor: 'shadow-green-500/40'
  },
  {
    step: '2',
    title: 'நேரம் ஒதுக்கவும்',
    desc: 'உங்களுக்கு வசதியான நேரத்தை தேர்வு செய்யுங்கள்',
    icon: Calendar,
    bgColor: 'from-red-500 to-rose-600',
    shadowColor: 'shadow-red-500/40'
  },
  {
    step: '3',
    title: 'அருள்வாக்கு பெறவும்',
    desc: 'அம்மனின் துல்லியமான வாக்கு மற்றும் பரிகாரம்',
    icon: Star,
    bgColor: 'from-amber-500 to-orange-500',
    shadowColor: 'shadow-amber-500/40'
  }];


  return (
    <section
      data-testid="how-it-works-section"
      className="relative py-16 px-4 overflow-hidden">

      {/* Vibrant Teal/Green background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#ecfdf5] via-[#d1fae5] to-[#ecfdf5]" />
      
      {/* Rangoli pattern corners */}
      <div className="absolute top-0 left-0 w-32 h-32 opacity-10">
        <div className="w-full h-full bg-gradient-to-br from-orange-500 to-transparent rounded-full transform -translate-x-1/2 -translate-y-1/2" />
      </div>
      <div className="absolute bottom-0 right-0 w-32 h-32 opacity-10">
        <div className="w-full h-full bg-gradient-to-tl from-red-500 to-transparent rounded-full transform translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10">

          <motion.span
            className="text-3xl mb-2 block"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}>

            ✨
          </motion.span>
          <h2 className="font-tamil-heading text-transparent bg-clip-text bg-gradient-to-r from-emerald-800 via-teal-700 to-emerald-800 text-xl sm:text-2xl font-bold">
            எப்படி செயல்படுகிறது?
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="flex flex-col md:flex-row gap-8 justify-center items-center">

          {steps.map((step, index) =>
          <motion.div
            key={index}
            variants={fadeInUp}
            data-testid={`step-card-${index}`}
            className="flex flex-col items-center text-center relative">

              {/* Connecting line for desktop */}
              {index < steps.length - 1 &&
            <div className="hidden md:block absolute top-8 left-full w-full h-1 bg-gradient-to-r from-orange-500 to-yellow-400" style={{ width: '100%' }} />
            }
              
              {/* Icon circle with glow */}
              <motion.div
              className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.bgColor} flex items-center justify-center mb-4 shadow-xl ${step.shadowColor}`}
              whileHover={{ scale: 1.1, rotate: 5 }}
              animate={pulseGlow.animate}>

                <step.icon className="w-8 h-8 text-white" />
              </motion.div>

              {/* Step number badge */}
              <motion.div
              className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-lg"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}>

                {step.step}
              </motion.div>

              <h3 className="font-tamil-heading text-red-900 text-base font-bold mb-1">
                {step.title}
              </h3>
              <p className="font-tamil-body text-red-800/70 text-xs max-w-[160px]">
                {step.desc}
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Pricing Section
const PricingSection = () =>
<section
  data-testid="pricing-section"
  className="relative py-16 px-4 overflow-hidden">

    {/* Deep saturated red gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-[#7f1d1d] via-[#991b1b] to-[#7f1d1d]" />
    
    {/* Animated mandala pattern */}
    <motion.div
    className="absolute inset-0 opacity-10"
    style={{
      backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/153089d5-352e-4aad-8ae1-0750b2d92f28/images/aec32164d7f76434e75c80e9111f54ba0d881b17114be9fb4bc06197a53af2fd.png')`,
      backgroundSize: 'cover'
    }}
    animate={{ opacity: [0.1, 0.15, 0.1] }}
    transition={{ duration: 5, repeat: Infinity }} />


    {/* Floating Diyas */}
    <Diya className="top-10 left-10" delay={0} />
    <Diya className="top-10 right-10" delay={0.3} />
    <Diya className="bottom-10 left-1/4" delay={0.6} />
    <Diya className="bottom-10 right-1/4" delay={0.9} />

    <div className="relative z-10 max-w-4xl mx-auto">
      <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center mb-8">

        <motion.span
        className="text-3xl mb-2 block"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>

          🪷
        </motion.span>
        <h2 className="font-tamil-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-300 text-2xl sm:text-3xl font-bold">
          காணிக்கை
        </h2>
      </motion.div>

      <div className="flex flex-col md:flex-row items-center justify-center gap-8">
        {/* Price Badge */}
        <motion.div
        initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.05, rotate: 2 }}
        data-testid="pricing-card"
        className="relative">

          {/* Glow effect */}
          <motion.div
          className="absolute inset-0 bg-amber-400 rounded-2xl blur-xl"
          animate={{ opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }} />

          <div className="relative bg-gradient-to-br from-yellow-400 via-orange-500 to-yellow-600 rounded-2xl p-8 text-center shadow-2xl scale-110">
            <p className="font-tamil-body text-red-900 text-sm font-bold">ரூபாய்</p>
            <motion.div
            className="font-tamil-heading text-red-900 text-6xl sm:text-7xl font-black"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: Infinity }}>

              300/-
            </motion.div>
            <p className="font-tamil-body text-red-900 text-sm font-bold">மட்டுமே</p>
          </div>
        </motion.div>

        {/* Description */}
        <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="text-center md:text-left">

          <h3 className="font-tamil-heading text-white text-xl sm:text-2xl font-bold mb-2 drop-shadow-lg">
            நேரில் வரத் தேவையில்லை -
          </h3>
          <p className="font-tamil-body text-amber-200 text-lg">
            உங்கள் போனிலேயே தீர்வு!
          </p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start">
            <span className="text-xs bg-white/10 text-amber-200 px-3 py-1 rounded-full border border-amber-400/30">
              ✓ போன் ஆலோசனை
            </span>
            <span className="text-xs bg-white/10 text-amber-200 px-3 py-1 rounded-full border border-amber-400/30">
              ✓ தனிப்பட்ட பரிகாரம்
            </span>
          </div>
        </motion.div>
      </div>

      {/* Testimonials */}
      <motion.div
      variants={staggerContainer}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true }}
      className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">

        {[
      { name: 'திருமதி. கவிதா', text: 'அம்மன் அருள்வாக்கு என் குடும்பப் பிரச்சனையை தீர்த்து வைத்தது.' },
      { name: 'திரு. முருகன்', text: 'தொழிலில் வந்த தடைகள் நீங்கி வளர்ச்சி கிடைத்தது.' },
      { name: 'திருமதி. லட்சுமி', text: 'பல ஆண்டுகால கஷ்டம் ஒரே முறையில் தீர்வானது.' }].
      map((testimonial, index) =>
      <motion.div
        key={index}
        variants={fadeInUp}
        whileHover={{ y: -5, scale: 1.02 }}
        data-testid={`testimonial-card-${index}`}
        className="bg-gradient-to-br from-red-900/60 to-red-950/60 backdrop-blur-sm border border-amber-500/30 rounded-xl p-5 text-center">

            <div className="flex justify-center gap-1 mb-3">
              {[...Array(5)].map((_, i) =>
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 * i }}>

                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                </motion.div>
          )}
            </div>
            <p className="font-tamil-body text-amber-100/90 text-sm leading-relaxed mb-3">
              "{testimonial.text}"
            </p>
            <p className="font-tamil-body text-amber-400 font-semibold text-sm">
              - {testimonial.name}
            </p>
          </motion.div>
      )}
      </motion.div>

      {/* CTA Button */}
      <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center mt-10">

        <motion.a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-pricing-btn"
        whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(37, 211, 102, 0.5)" }}
        whileTap={{ scale: 0.95 }}
        className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-tamil-body font-bold text-base sm:text-lg px-10 py-4 rounded-full transition-all duration-300 shadow-xl shadow-green-500/30">

          <motion.span
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1, repeat: Infinity }}>

            <MessageCircle className="w-6 h-6" />
          </motion.span>
          <span>இப்போதே ஆலோசனையைப் பெற</span>
        </motion.a>
      </motion.div>
    </div>
  </section>;


// Trust Section with Testimonials - Simplified
const TrustSection = () => {
  return (
    <section
      data-testid="trust-section"
      className="relative py-14 px-6 overflow-hidden">

      {/* Purple/Spiritual gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#f5f3ff] via-[#ede9fe] to-[#f5f3ff]" />
      
      {/* Decorative elements */}
      <motion.div
        className="absolute top-4 left-1/2 -translate-x-1/2"
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 3, repeat: Infinity }}>

        <span className="text-4xl">🙏</span>
      </motion.div>

      <div className="relative z-10 max-w-4xl mx-auto text-center pt-8">
        {/* Logo/Brand */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}>

          <h3 className="font-tamil-heading text-transparent bg-clip-text bg-gradient-to-r from-purple-800 via-indigo-700 to-purple-800 text-2xl font-bold">நேரடியாக தொடர்புக்கொள்ள
          </h3>
          <motion.a
            href={`tel:+${WHATSAPP_NUMBER}`}
            className="inline-flex items-center gap-2 text-[#25D366] font-bold mt-3 text-lg"
            whileHover={{ scale: 1.05 }}>

            <Phone className="w-5 h-5" />
            +91 6381326346
          </motion.a>
        </motion.div>

        {/* Trust Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 px-6 py-2 rounded-full border border-green-300 shadow-sm">

          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="font-tamil-body font-semibold">நம்பகத்தன்மை - 100% ரகசியம்</span>
        </motion.div>

        {/* Decorative line */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-amber-400" />
          <span className="text-amber-500">🪷</span>
          <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-amber-400" />
        </div>
      </div>
    </section>);

};

// Footer Section
const FooterSection = () =>
<footer
  data-testid="footer-section"
  className="relative py-8 px-6 overflow-hidden">

    {/* Deep saturated red gradient background */}
    <div className="absolute inset-0 bg-gradient-to-b from-[#7f1d1d] to-[#450a0a]" />
    
    <div className="relative z-10 max-w-5xl mx-auto text-center">
      {/* Decorative Om */}
      <motion.div
      className="mb-4"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 3, repeat: Infinity }}>

        <span className="text-2xl text-amber-500/50">🔱</span>
      </motion.div>

      <div className="flex justify-center gap-4 text-xs text-amber-200/50">
        <Link
          to="/Privacy Policy"
          className="font-tamil-body hover:text-amber-400 transition-colors">
          Privacy Policy
        </Link>
        <span className="text-amber-500/30">|</span>
        <Link
          to="/Terms and conditions"
          className="font-tamil-body hover:text-amber-400 transition-colors">
          Terms and conditions
        </Link>
        <span className="text-amber-500/30">|</span>
        <Link
          to="/Contact Us"
          className="font-tamil-body hover:text-amber-400 transition-colors">
          Contact Us
        </Link>
      </div>

      <p className="font-tamil-body text-amber-200/40 text-[10px] sm:text-xs max-w-2xl mx-auto mt-4 px-4 italic">
        பொறுப்புத் துறப்பு: "இந்த ஆலோசனைகள் நம்பிக்கை சார்ந்தவை. முடிவுகள் ஒவ்வொருவரின் ஜாதகம் மற்றும் கர்ம வினையைப் பொறுத்து மாறுபடலாம்"
      </p>
      <p className="font-tamil-body text-amber-200/30 text-xs mt-6">
        © 2026 மேல்மலையனுர் அங்காளம்மன். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.
      </p>
    </div>
  </footer>;


// Floating WhatsApp Button
// Floating Contact Group
const FloatingContactGroup = () => {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-4">
      {/* Call Button */}
      <motion.a
        href={`tel:${WHATSAPP_NUMBER}`}
        initial={{ opacity: 0, scale: 0, x: 20 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="w-14 h-14 bg-gradient-to-br from-violet-600 to-indigo-700 rounded-full flex items-center justify-center cursor-pointer shadow-xl border-2 border-white/20"
        title="Call Now">
        <Phone className="w-7 h-7 text-white" />
      </motion.a>

      {/* WhatsApp Button */}
      <motion.a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, scale: 0, x: 20 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center cursor-pointer shadow-xl border-2 border-white/20"
        title="Chat on WhatsApp">
        <WhatsAppIcon className="w-10 h-10 text-white" />
        <span className="sr-only">WhatsApp (Original Logo)</span>
      </motion.a>
    </div>
  );
};


// Main Landing Page Component
const AngalammanLanding = () => {
  return (
    <div
      data-testid="angalamman-landing"
      className="min-h-screen bg-[#2A0505] font-tamil-body">

      <HeaderNav />
      <HeroSection />
      <DivineDarshanSection />
      <ServicesSection />
      <HowItWorksSection />
      <PricingSection />
      <TrustSection />
      <FooterSection />
      <FloatingContactGroup />
    </div>
  );
};

export default AngalammanLanding;